import { useState, useEffect, useCallback, useRef } from "react";
import { Mail, Phone, Linkedin, MapPin, Calendar, ExternalLink, Award, BookOpen, Users, Heart, GraduationCap, Briefcase, ChevronRight, Moon, Sun, Download, ArrowUp, Loader2, Scale, X } from "lucide-react";
import { resumeData } from "../data/resumeData";
import { Badge } from "../components/ui/badge";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import html2pdf from "html2pdf.js";

/* ── tiny animated wrapper ── */
const AnimatedSection = ({ children, className = "", direction = "up", startVisible = false }) => {
  const [ref, visible] = useScrollAnimation(0.1, startVisible);
  const hiddenClass = direction === "left" ? "scroll-hidden-left" : direction === "right" ? "scroll-hidden-right" : "scroll-hidden";
  const visibleClass = direction === "left" ? "scroll-visible-left" : direction === "right" ? "scroll-visible-right" : "scroll-visible";
  return (
    <div ref={ref} className={`${visible ? visibleClass : hiddenClass} ${className}`}>
      {children}
    </div>
  );
};

/* ── section heading ── */
const SectionHeader = ({ label, icon: Icon }) => (
  <div className="flex items-center gap-3 mb-10" data-testid={`section-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--resume-accent)" }}>
      <Icon className="w-5 h-5" style={{ color: "var(--resume-nav-text)" }} />
    </div>
    <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-medium tracking-tight" style={{ color: "var(--resume-text)" }}>
      {label}
    </h2>
    <div className="flex-1 h-px ml-4" style={{ background: "var(--resume-border)" }} />
  </div>
);

/* ── internship card ── */
const InternshipCard = ({ internship, index }) => {
  const [ref, visible] = useScrollAnimation(0.08);
  return (
    <div
      ref={ref}
      className={`group grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 py-10 first:pt-0 last:border-b-0 ${visible ? "scroll-visible" : "scroll-hidden"}`}
      style={{ borderBottom: "1px solid var(--resume-border)", transitionDelay: `${(index % 4) * 60}ms` }}
      data-testid={`internship-${index}`}
    >
      <div className="lg:col-span-4 space-y-2">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--resume-accent)" }}>
          <Calendar className="w-3.5 h-3.5" />
          <span>{internship.duration}</span>
          <span style={{ color: "var(--resume-border)" }}>|</span>
          <span className="font-medium normal-case tracking-normal" style={{ color: "var(--resume-text-secondary)" }}>{internship.period}</span>
        </div>
        <h3 className="font-['Outfit'] text-xl sm:text-2xl font-semibold leading-tight" style={{ color: "var(--resume-text)" }}>
          {internship.company}
        </h3>
        <div className="flex items-center gap-1.5 text-sm" style={{ color: "var(--resume-text-secondary)" }}>
          <MapPin className="w-3.5 h-3.5" />
          <span>{internship.location}</span>
        </div>
        {internship.team && (
          <span
            className="inline-block mt-1 text-xs font-semibold tracking-wide uppercase rounded-full px-3 py-1"
            style={{ color: "var(--resume-accent)", background: "var(--resume-accent-light)", border: "1px solid var(--resume-accent-border)" }}
          >
            {internship.team}
          </span>
        )}
      </div>
      <div className="lg:col-span-8">
        {internship.description.length > 0 ? (
          <ul className="space-y-3">
            {internship.description.map((item, i) => (
              <li key={i} className="flex gap-3 text-[15px] leading-relaxed" style={{ color: "var(--resume-text-secondary)" }}>
                <ChevronRight className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "var(--resume-accent)" }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm italic" style={{ color: "var(--resume-text-secondary)" }}>Currently interning</p>
        )}
      </div>
    </div>
  );
};

/* ── sidebar sections config ── */
const SECTIONS = [
  { id: "hero-section", label: "Home" },
  { id: "academics-section", label: "Academics" },
  { id: "internships-section", label: "Internships" },
  { id: "publications-section", label: "Publications" },
  { id: "certifications-section", label: "Certifications" },
  { id: "co-curricular-section", label: "Activities" },
  { id: "positions-section", label: "Positions" },
  { id: "extra-curricular-section", label: "Achievements" },
  { id: "volunteering-section", label: "Volunteering" },
  { id: "personal-details-section", label: "Personal" },
];

/* ── main page ── */
export default function ResumePage() {
  const d = resumeData;
  const [dark, setDark] = useState(true);
  const [activeSection, setActiveSection] = useState("hero-section");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const resumeRef = useRef(null);

  const SPORTS_LAW_PDF = "https://customer-assets.emergentagent.com/job_lawyer-profile-hub/artifacts/758zdobp_Abhirup%20Verma-%20Resume.pdf";
  const IP_LAW_PDF = "https://customer-assets.emergentagent.com/job_lawyer-profile-hub/artifacts/jupqh5sv_Abhirup%20Verma-%20Resume.pdf";

  /* track scroll for Back to Top */
  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* track which section is in view */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveSection(e.target.dataset.testid);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.querySelector(`[data-testid="${id}"]`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = useCallback((id) => {
    document.querySelector(`[data-testid="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleDownloadSportsLaw = () => {
    const link = document.createElement("a");
    link.href = SPORTS_LAW_PDF;
    link.download = "Abhirup_Verma_Sports_Law_Resume.pdf";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowPdfModal(false);
  };

  const handleDownloadIPLaw = () => {
    const link = document.createElement("a");
    link.href = IP_LAW_PDF;
    link.download = "Abhirup_Verma_IP_Law_Resume.pdf";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowPdfModal(false);
  };

  const toggleDark = () => setDark((p) => !p);

  return (
    <div ref={resumeRef} className={`min-h-screen transition-colors duration-400 ${dark ? "dark-mode" : ""}`} style={{ background: "var(--resume-bg)" }} data-testid="resume-page">

      {/* ── Sticky Nav ── */}
      <nav className="sticky top-0 z-50 no-print nav-entrance" style={{ background: "var(--resume-nav-bg)" }} data-testid="contact-bar">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-3 flex flex-wrap items-center justify-between gap-3 text-sm" style={{ color: "var(--resume-nav-text)" }}>
          <span className="font-['Outfit'] font-semibold tracking-wide text-base">{d.name}</span>
          <div className="flex flex-wrap items-center gap-4">
            <a href={`mailto:${d.contact.email}`} className="flex items-center gap-1.5 hover:opacity-70 transition-opacity" data-testid="contact-email">
              <Mail className="w-3.5 h-3.5" /><span className="hidden sm:inline">{d.contact.email}</span>
            </a>
            <a href={`tel:${d.contact.phone}`} className="flex items-center gap-1.5 hover:opacity-70 transition-opacity" data-testid="contact-phone">
              <Phone className="w-3.5 h-3.5" /><span className="hidden sm:inline">{d.contact.phone}</span>
            </a>
            <a href={d.contact.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:opacity-70 transition-opacity" data-testid="contact-linkedin">
              <Linkedin className="w-3.5 h-3.5" /><span className="hidden md:inline">LinkedIn</span>
            </a>
            <button onClick={() => setShowPdfModal(true)} disabled={pdfLoading} className="flex items-center gap-1.5 hover:opacity-70 transition-opacity cursor-pointer disabled:opacity-50" data-testid="download-pdf-btn" title="Download as PDF">
              {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}<span className="hidden lg:inline">{pdfLoading ? "Generating..." : "PDF"}</span>
            </button>
            <button onClick={toggleDark} className="ml-1 p-1.5 rounded-full hover:opacity-70 transition-opacity cursor-pointer" data-testid="dark-mode-toggle" title="Toggle dark mode">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </nav>

      {/* ── PDF Choice Modal ── */}
      {showPdfModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center no-print" data-testid="pdf-modal-overlay">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowPdfModal(false)} />
          <div
            className="relative rounded-2xl p-8 max-w-md w-[90%] shadow-2xl"
            style={{ background: "var(--resume-surface)", border: "1px solid var(--resume-border)" }}
            data-testid="pdf-modal"
          >
            <button
              onClick={() => setShowPdfModal(false)}
              className="absolute top-4 right-4 p-1 rounded-full hover:opacity-60 transition-opacity cursor-pointer"
              style={{ color: "var(--resume-text-secondary)" }}
              data-testid="pdf-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-['Outfit'] text-xl font-semibold mb-2" style={{ color: "var(--resume-text)" }}>
              Download Resume
            </h3>
            <p className="text-sm mb-6" style={{ color: "var(--resume-text-secondary)" }}>
              Choose a resume version to download
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleDownloadSportsLaw}
                className="flex-1 flex items-center justify-center gap-3 py-4 px-6 rounded-xl text-sm font-medium border transition-all duration-200 hover:scale-[1.02] cursor-pointer"
                style={{ background: "transparent", borderColor: "var(--resume-accent)", color: "var(--resume-accent)" }}
                data-testid="pdf-sports-law-btn"
              >
                <span className="text-lg" style={{ filter: "grayscale(1)" }}>🏏</span>
                Sports Law
              </button>
              <button
                onClick={handleDownloadIPLaw}
                className="flex-1 flex items-center justify-center gap-3 py-4 px-6 rounded-xl text-sm font-medium border transition-all duration-200 hover:scale-[1.02] cursor-pointer"
                style={{ background: "transparent", borderColor: "var(--resume-accent)", color: "var(--resume-accent)" }}
                data-testid="pdf-ip-law-btn"
              >
                <span className="text-lg" style={{ filter: "grayscale(1)" }}>💡</span>
                IP Law
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sidebar Navigation ── */}
      <div className="fixed left-4 top-1/2 -translate-y-1/2 z-40 hidden xl:flex flex-col items-center gap-1 no-print" data-testid="sidebar-nav">
        {SECTIONS.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => scrollTo(id)}
            className="group flex items-center gap-3 py-1.5 cursor-pointer"
            data-testid={`sidebar-${id}`}
          >
            <div
              className="w-2.5 h-2.5 rounded-full transition-all duration-300"
              style={{
                background: activeSection === id ? "var(--resume-sidebar-active)" : "var(--resume-sidebar-dot)",
                transform: activeSection === id ? "scale(1.4)" : "scale(1)",
              }}
            />
            <span
              className="text-xs font-medium opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all duration-200 whitespace-nowrap"
              style={{ color: activeSection === id ? "var(--resume-accent)" : "var(--resume-text-secondary)" }}
            >
              {label}
            </span>
          </button>
        ))}
      </div>

      {/* ── Hero ── */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-20 pb-16 md:pt-28 md:pb-24" data-testid="hero-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-7 space-y-6">
            <p className="hero-entrance hero-delay-1 uppercase tracking-[0.25em] text-xs font-bold" style={{ color: "var(--resume-text-secondary)" }}>Advocate</p>
            <h1 className="hero-entrance-left hero-delay-2 font-['Outfit'] text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light leading-[0.95]" style={{ color: "var(--resume-text)" }}>
              Abhirup<br /><span className="font-semibold">Verma</span>
            </h1>
            <div className="hero-entrance hero-delay-3 flex flex-wrap gap-2 pt-2">
              {d.specializations.map((s) => (
                <Badge
                  key={s}
                  variant="outline"
                  className="rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-300 hover:scale-105 cursor-default"
                  style={{ background: "var(--resume-badge-bg)", borderColor: "var(--resume-badge-border)", color: "var(--resume-badge-text)" }}
                  data-testid={`specialization-${s.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {s}
                </Badge>
              ))}
            </div>
            <p className="hero-entrance hero-delay-4 text-base sm:text-lg leading-relaxed max-w-lg pt-2 text-justify" style={{ color: "var(--resume-text-secondary)" }}>
              A BBA LLB (Hons.) graduate of Symbiosis Law School, Pune and a qualified advocate enrolled with the Bar Council of Delhi (Enrollment No. D/4204/2026) and member of the Delhi High Court Bar Association, having passed the All India Bar Examination (AIBE-XX), with extensive experience across top-tier law firms, corporate legal teams, and courts.
            </p>
          </div>
          <div className="md:col-span-5 flex justify-center md:justify-end hero-entrance-right hero-delay-5">
            <div className="w-72 h-80 sm:w-80 sm:h-[22rem] rounded-2xl overflow-hidden relative">
              <img
                src={d.photo}
                alt="Abhirup Verma"
                className="absolute w-full object-cover"
                style={{ mixBlendMode: dark ? "normal" : "multiply", top: "-18%", height: "135%" }}
                data-testid="hero-photo"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto h-px" style={{ background: "var(--resume-border)" }} />

      {/* ── Academics ── */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-24" data-testid="academics-section">
        <AnimatedSection>
          <SectionHeader label="Academic Qualifications" icon={GraduationCap} />
        </AnimatedSection>
        {d.academics.map((a, i) => (
          <AnimatedSection key={i}>
            <div className={`grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-8 py-5 stagger-${i + 1}`} style={{ borderBottom: i < d.academics.length - 1 ? "1px solid var(--resume-border)" : "none" }} data-testid={`academic-${i}`}>
              <div className="sm:col-span-3">
                <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--resume-accent)" }}>{a.year}</span>
              </div>
              <div className="sm:col-span-9 space-y-1">
                <h3 className="font-['Outfit'] text-lg font-semibold" style={{ color: "var(--resume-text)" }}>{a.institution}</h3>
                <p className="text-sm" style={{ color: "var(--resume-text-secondary)" }}>{a.degree}</p>
                <p className="text-sm" style={{ color: "var(--resume-text-secondary)" }}>{a.details}</p>
              </div>
            </div>
          </AnimatedSection>
        ))}
      </section>

      <div className="max-w-6xl mx-auto h-px" style={{ background: "var(--resume-border)" }} />

      {/* ── Internships ── */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-24" data-testid="internships-section">
        <AnimatedSection>
          <SectionHeader label="Internship Experience" icon={Briefcase} />
          <p className="text-sm mb-8 -mt-4" style={{ color: "var(--resume-text-secondary)" }}>
            {d.internships.length} internships across leading law firms, corporates, and courts
          </p>
        </AnimatedSection>
        {d.internships.map((internship, i) => (
          <InternshipCard key={i} internship={internship} index={i} />
        ))}
      </section>

      <div className="max-w-6xl mx-auto h-px" style={{ background: "var(--resume-border)" }} />

      {/* ── Publications ── */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-24" data-testid="publications-section">
        <AnimatedSection>
          <SectionHeader label="Publications" icon={BookOpen} />
        </AnimatedSection>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {d.publications.map((p, i) => (
            <AnimatedSection key={i} className={`stagger-${i + 1}`}>
              <div
                className="border rounded-xl p-6 space-y-3 hover:shadow-lg transition-shadow duration-300 h-full"
                style={{ background: "var(--resume-surface)", borderColor: "var(--resume-border)" }}
                data-testid={`publication-${i}`}
              >
                <span className="text-xs font-bold uppercase tracking-[0.15em]" style={{ color: "var(--resume-accent)" }}>{p.journal}</span>
                <h3 className="font-['Outfit'] text-[15px] font-semibold leading-snug" style={{ color: "var(--resume-text)" }}>{p.title}</h3>
                {p.url && (
                  <a href={p.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: "var(--resume-accent)" }} data-testid={`publication-link-${i}`}>
                    Read Article <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      <div className="max-w-6xl mx-auto h-px" style={{ background: "var(--resume-border)" }} />

      {/* ── Certifications ── */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-24" data-testid="certifications-section">
        <AnimatedSection>
          <SectionHeader label="Certifications" icon={Award} />
        </AnimatedSection>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {d.certifications.map((c, i) => (
            <AnimatedSection key={i} className={`stagger-${Math.min(i + 1, 8)}`}>
              <div className="flex items-start gap-3 py-3 px-4 border rounded-lg" style={{ background: "var(--resume-surface)", borderColor: "var(--resume-border)" }} data-testid={`certification-${i}`}>
                <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: "var(--resume-accent)" }} />
                <span className="text-[15px]" style={{ color: "var(--resume-text-secondary)" }}>{c}</span>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      <div className="max-w-6xl mx-auto h-px" style={{ background: "var(--resume-border)" }} />

      {/* ── Co-Curricular ── */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-24" data-testid="co-curricular-section">
        <AnimatedSection>
          <SectionHeader label="Co-Curricular Activities" icon={BookOpen} />
        </AnimatedSection>
        <div className="space-y-4">
          {d.coCurricular.map((item, i) => (
            <AnimatedSection key={i} className={`stagger-${i + 1}`}>
              <div className="flex items-start gap-3 text-[15px] leading-relaxed" style={{ color: "var(--resume-text-secondary)" }} data-testid={`co-curricular-${i}`}>
                <ChevronRight className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "var(--resume-accent)" }} />
                <span>{item}</span>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      <div className="max-w-6xl mx-auto h-px" style={{ background: "var(--resume-border)" }} />

      {/* ── Positions ── */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-24" data-testid="positions-section">
        <AnimatedSection>
          <SectionHeader label="Positions of Responsibility" icon={Users} />
        </AnimatedSection>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {d.positions.map((p, i) => (
            <AnimatedSection key={i} className={`stagger-${Math.min(i + 1, 8)}`}>
              <div className="flex items-start gap-3 py-3 px-4 border rounded-lg" style={{ background: "var(--resume-surface)", borderColor: "var(--resume-border)" }} data-testid={`position-${i}`}>
                <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: "var(--resume-accent)" }} />
                <span className="text-[15px]" style={{ color: "var(--resume-text-secondary)" }}>{p}</span>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      <div className="max-w-6xl mx-auto h-px" style={{ background: "var(--resume-border)" }} />

      {/* ── Extra-Curricular ── */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-24" data-testid="extra-curricular-section">
        <AnimatedSection>
          <SectionHeader label="Extra-Curricular Achievements" icon={Award} />
        </AnimatedSection>
        <div className="space-y-4">
          {d.extraCurricular.map((item, i) => (
            <AnimatedSection key={i} className={`stagger-${i + 1}`}>
              <div className="flex items-start gap-3 text-[15px] leading-relaxed" style={{ color: "var(--resume-text-secondary)" }} data-testid={`extra-curricular-${i}`}>
                <ChevronRight className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "var(--resume-accent)" }} />
                <span>{item}</span>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      <div className="max-w-6xl mx-auto h-px" style={{ background: "var(--resume-border)" }} />

      {/* ── Volunteering ── */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-24" data-testid="volunteering-section">
        <AnimatedSection>
          <SectionHeader label="Volunteering" icon={Heart} />
        </AnimatedSection>
        <div className="space-y-4">
          {d.volunteering.map((item, i) => (
            <AnimatedSection key={i} className={`stagger-${i + 1}`}>
              <div className="flex items-start gap-3 text-[15px] leading-relaxed" style={{ color: "var(--resume-text-secondary)" }} data-testid={`volunteering-${i}`}>
                <ChevronRight className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "var(--resume-accent)" }} />
                <span>{item}</span>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      <div className="max-w-6xl mx-auto h-px" style={{ background: "var(--resume-border)" }} />

      {/* ── Personal Details ── */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-24" data-testid="personal-details-section">
        <AnimatedSection>
          <SectionHeader label="Personal Details" icon={Users} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--resume-text-secondary)" }}>Areas of Interest</p>
              <div className="flex flex-wrap gap-2">
                {d.personalDetails.areasOfInterest.map((a) => (
                  <Badge key={a} variant="outline" className="rounded-full px-4 py-1.5 text-sm" style={{ background: "var(--resume-badge-bg)", borderColor: "var(--resume-badge-border)", color: "var(--resume-badge-text)" }}>
                    {a}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--resume-text-secondary)" }}>Languages</p>
              <div className="flex flex-wrap gap-2">
                {d.personalDetails.languages.map((l) => (
                  <Badge key={l} variant="outline" className="rounded-full px-4 py-1.5 text-sm" style={{ background: "var(--resume-badge-bg)", borderColor: "var(--resume-badge-border)", color: "var(--resume-badge-text)" }}>
                    {l}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* ── Back to Top ── */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="fixed bottom-8 right-8 z-40 no-print w-11 h-11 rounded-full flex items-center justify-center shadow-lg cursor-pointer transition-all duration-300"
        style={{
          background: "var(--resume-accent)",
          color: "var(--resume-nav-text)",
          opacity: showBackToTop ? 1 : 0,
          pointerEvents: showBackToTop ? "auto" : "none",
          transform: showBackToTop ? "translateY(0) scale(1)" : "translateY(16px) scale(0.8)",
        }}
        data-testid="back-to-top-btn"
        title="Back to top"
      >
        <ArrowUp className="w-5 h-5" />
      </button>

      {/* ── Footer ── */}
      <footer className="py-12 transition-colors duration-400" style={{ background: "var(--resume-nav-bg)" }} data-testid="footer">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 flex flex-col md:flex-row items-center justify-between gap-6" style={{ color: "var(--resume-nav-text)" }}>
          <div className="space-y-1 text-center md:text-left">
            <p className="font-['Outfit'] text-xl font-semibold">{d.name}</p>
            <p className="text-sm" style={{ color: "var(--resume-text-secondary)" }}>Advocate | Bar Council of Delhi (D/4204/2026)</p>
          </div>
          <div className="flex items-center gap-6">
            <a href={`mailto:${d.contact.email}`} className="hover:opacity-70 transition-opacity" data-testid="footer-email"><Mail className="w-5 h-5" /></a>
            <a href={`tel:${d.contact.phone}`} className="hover:opacity-70 transition-opacity" data-testid="footer-phone"><Phone className="w-5 h-5" /></a>
            <a href={d.contact.linkedin} target="_blank" rel="noopener noreferrer" className="hover:opacity-70 transition-opacity" data-testid="footer-linkedin"><Linkedin className="w-5 h-5" /></a>
          </div>
        </div>
      </footer>
    </div>
  );
}
